import { useEffect } from "react";
import { Loader2 } from "lucide-react";
import { MdOutlineSpeed } from "react-icons/md";
import { toast } from "sonner";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Label } from "@/frontend/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/frontend/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/frontend/components/ui/alert-dialog";
import { useDailyCapStore } from "@/frontend/stores/dailyCapStore";

const POLL_MS = 60_000;
const number = new Intl.NumberFormat("en-IN");

export function DailyCapBanner({ orgId, collapseOnMobile = false }: { orgId: string; collapseOnMobile?: boolean }) {
  const {
    status, busy, resumeOpen, resumeCapDraft, dropOpen,
    fetchStatus, setResumeOpen, setDropOpen, setDrafts, resume, drop,
  } = useDailyCapStore();

  useEffect(() => {
    void fetchStatus(orgId);
    const interval = window.setInterval(() => void fetchStatus(orgId), POLL_MS);
    return () => window.clearInterval(interval);
  }, [orgId, fetchStatus]);

  if (!status?.pausedAt) return null;

  const queuedTotal = status.queued.reduce((sum, item) => sum + item.count, 0);
  const pausedAt = new Date(status.pausedAt).toLocaleTimeString("en-IN", {
    timeZone: status.timezone, hour: "numeric", minute: "2-digit",
  });
  const heading = status.cap === null
    ? "Daily WhatsApp send cap reached"
    : `Daily WhatsApp send cap reached (${number.format(status.cap)})`;
  const detail = queuedTotal > 0
    ? `Automated sending paused at ${pausedAt}. ${number.format(queuedTotal)} message${queuedTotal === 1 ? "" : "s"} queued across ${status.queued.length} campaign${status.queued.length === 1 ? "" : "s"}.`
    : `Automated sending paused at ${pausedAt}. Scheduled campaign steps are waiting.`;
  const quotaFull = status.cap !== null && status.sentToday >= status.cap;

  const handleResume = async () => {
    const error = await resume();
    if (error) toast.error(error);
    else toast.success("Sending resumed");
  };
  const handleDrop = async () => {
    const result = await drop();
    if ("error" in result) toast.error(result.error);
    else toast.success(`${number.format(result.dropped)} queued message${result.dropped === 1 ? "" : "s"} dropped`);
  };

  return (
    <>
      <div
        role="alert"
        className={`${collapseOnMobile ? "hidden md:block" : ""} border-b border-border bg-card px-4 py-3 shadow-[inset_3px_0_0_0_theme(colors.red.500)]`}
      >
        <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-5">
          <div className="flex min-w-0 items-start gap-3 sm:items-center">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-700 dark:bg-red-400/10 dark:text-red-300">
              <MdOutlineSpeed className="h-[18px] w-[18px]" />
            </span>
            <div className="min-w-0 text-sm">
              <p className="font-semibold text-foreground">{heading}</p>
              <p className="mt-0.5 leading-5 text-muted-foreground">{detail}</p>
              {!status.canManage && (
                <p className="mt-0.5 leading-5 text-muted-foreground">Ask an admin to resume or drop the queued messages.</p>
              )}
            </div>
          </div>
          {status.canManage && (
            <div className="flex items-center gap-2 sm:shrink-0">
              {queuedTotal > 0 && (
                <Button variant="outline" onClick={() => setDropOpen(true)} disabled={busy}>
                  Drop queued
                </Button>
              )}
              <Button onClick={() => setResumeOpen(true)} disabled={busy} className="bg-red-600 text-white hover:bg-red-700">
                Resume
              </Button>
            </div>
          )}
        </div>
      </div>
      {collapseOnMobile && (
        <details className="border-b border-border bg-card md:hidden">
          <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2 px-4 text-sm font-semibold text-red-700 dark:text-red-300">
            <MdOutlineSpeed className="h-4 w-4 shrink-0" />
            <span className="flex-1">Daily send cap reached</span>
          </summary>
          <div className="space-y-3 px-4 pb-3 text-sm text-muted-foreground">
            <p>{detail}</p>
            {status.canManage && (
              <div className="flex gap-2">
                {queuedTotal > 0 && <Button size="sm" variant="outline" onClick={() => setDropOpen(true)}>Drop queued</Button>}
                <Button size="sm" onClick={() => setResumeOpen(true)}>Resume</Button>
              </div>
            )}
          </div>
        </details>
      )}

      <Dialog open={resumeOpen} onOpenChange={setResumeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resume automated sending</DialogTitle>
            <DialogDescription>
              {number.format(status.sentToday)} sent today ({status.day}, {status.timezone}).
              {quotaFull ? " Raise the cap to resume today, or resume after midnight when the count resets." : ""}
            </DialogDescription>
          </DialogHeader>
          {status.queued.length > 0 && (
            <ul className="max-h-40 space-y-1 overflow-y-auto rounded-md bg-muted/50 px-3 py-2 text-sm">
              {status.queued.map((item) => (
                <li key={item.campaignId} className="flex justify-between gap-3">
                  <span className="truncate">{item.name}</span>
                  <span className="shrink-0 tabular-nums text-muted-foreground">{number.format(item.count)}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="space-y-2">
            <Label htmlFor="daily-cap-resume">Daily cap</Label>
            <Input
              id="daily-cap-resume"
              inputMode="numeric"
              value={resumeCapDraft}
              onChange={(event) => setDrafts({ resumeCapDraft: event.target.value })}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResumeOpen(false)}>Cancel</Button>
            <Button onClick={() => void handleResume()} disabled={busy}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Resume sending
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={dropOpen} onOpenChange={setDropOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Drop {number.format(queuedTotal)} queued messages?</AlertDialogTitle>
            <AlertDialogDescription>
              These messages will not be sent and are not charged. Recipients are marked
              “Dropped: daily WhatsApp send cap reached” in each campaign. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleDrop()} disabled={busy} className="bg-red-600 hover:bg-red-700">
              Drop messages
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
